import React, { useEffect, useState, useRef, memo } from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { media } from '../../styles/media';
import { Icon } from './icons/IconSprite';

const float = keyframes`
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
  100% {
    transform: translateY(0px);
  }
`;

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const DecorationWrapper = styled.div`
  position: absolute;
  width: ${props => props.$size || '120px'};
  height: ${props => props.$size || '120px'};
  top: ${props => props.$top};
  left: ${props => props.$left};
  right: ${props => props.$right};
  bottom: ${props => props.$bottom};
  z-index: 0;
  animation: ${float} 6s ease-in-out infinite;
  opacity: ${props => props.$opacity || 0.5};
  pointer-events: none;
  
  ${media.md`
    width: ${props => props.$mobilSize || '80px'};
    height: ${props => props.$mobilSize || '80px'};
  `}
  
  svg {
    width: 100%;
    height: 100%;
    animation: ${rotate} ${props => props.$rotationSpeed || '20s'} linear infinite;
    fill: ${props => props.$color || 'currentColor'};
  }
  
  @media (max-width: 1200px) {
    display: none;
  }
`;

const Title = styled.div`
  margin-bottom: 20px;
  
  h3 {
    font-size: 24px;
    font-weight: 700;
    color: #2B2D42;
    margin: 0;
    line-height: 1.3;
    
    ${media.md`
      font-size: 20px;
    `}
  }
  
  .highlight {
    color: #FF4B8F;
  }
`;

const Features = styled.div`
  display: grid;
  gap: 16px;
  margin-bottom: 24px;
  
  ${media.sm`
    gap: 10px;
    margin-bottom: 16px;
  `}

  @media screen and (max-height: 450px) {
    gap: 8px;
    margin-bottom: 8px;
    
    & > div:nth-child(n+3) {
      display: none;
    }
  }
`;

const Feature = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  
  svg {
    width: 24px;
    height: 24px;
    color: #FF4B8F;
    flex-shrink: 0;
  }

  @media screen and (max-height: 450px) {
    gap: 8px;
    
    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const FeatureContent = styled.div`
  h4 {
    font-size: 16px;
    font-weight: 600;
    color: #2B2D42;
    margin: 0 0 4px;
  }
  
  p {
    font-size: 14px;
    color: #666;
    margin: 0;
    line-height: 1.4;
  }

  @media screen and (max-height: 450px) {
    h4 {
      font-size: 14px;
      margin: 0 0 2px;
    }
    
    p {
      font-size: 12px;
      line-height: 1.3;
    }
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  justify-content: center;
  
  @media screen and (max-height: 450px) {
    gap: 6px;
    margin-bottom: 8px;
  }
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  background: #F8F9FA;
  color: #2B2D42;
  text-decoration: none;
  transition: all 0.3s ease;
  white-space: nowrap;
  
  &:hover {
    background: #E9ECEF;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }

  @media (max-width: 768px) and (orientation: landscape),
         (max-width: 1024px) and (orientation: landscape) {
    padding: 8px;
    border-radius: 50%;
    min-width: 36px;
    justify-content: center;
    
    span {
      display: none;
    }
  }
`;

const MobileSocialLinks = styled.div`
  display: none;
  gap: 8px;
  justify-content: center;
  
  @media (max-width: 768px) and (orientation: landscape) {
    display: flex;
  }
`;

const MobileSocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #F8F9FA;
  color: #2B2D42;
  transition: all 0.3s ease;
  margin: 0 4px;
  
  &:hover {
    background: #E9ECEF;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const ActionButton = styled.a`
  position: relative;
  display: block;
  width: 100%;
  padding: 14px 24px;
  background: #FF4B8F;
  color: white;
  text-align: center;
  border-radius: 12px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #E62E6F;
    transform: translateY(-2px);
  }
`;

const FloatingBox = styled.div`
  position: fixed;
  right: 24px;
  bottom: 24px;
  background: white;
  border-radius: 24px;
  padding: 24px;
  box-shadow: 0 8px 32px rgba(43, 45, 66, 0.1);
  z-index: 100;
  width: 100%;
  max-width: 380px;

  // Десктопные стили
  ${media.xl`
    max-width: 340px;
  `}

  ${media.lg`
    max-width: 300px;
    padding: 20px;
  `}

  // Планшетные стили
  ${media.md`
    max-width: 280px;
    padding: 16px;
  `}

  // Портретная ориентация на планшетах
  @media (max-width: 1024px) and (orientation: portrait) {
    display: none;
  }

  // Мобильная версия
  @media (max-width: 768px) {
    margin-top: 60px;
    position: relative;
    
    // Стили для логотипа
    .logo {
      width: 140px; // Уменьшаем размер логотипа
      margin-bottom: 12px;
    }

    // Описание
    p {
      font-size: 12px;
      line-height: 1.3;
      margin: 8px 0;
    }

    // Quick Links
    h3 {
      font-size: 16px;
      margin-bottom: 8px;
    }

    // Ссылки в футере
    a {
      font-size: 14px;
      margin-bottom: 6px;
    }

    // Контакты
    .contact-info {
      font-size: 12px;
      margin: 6px 0;
    }

    // Социальные ссылки
    ${SocialLinks} {
      gap: 12px;
      margin: 8px 0;
      
      a {
        font-size: 14px;
        padding: 6px 10px;
      }
    }

    // Нижняя часть футера
    .footer-bottom {
      margin-top: 12px;
      padding-top: 12px;
      font-size: 12px;
      
      a {
        margin: 0 8px;
      }
    }
  }

  // Для очень маленьких экранов можем уменьшить отступ
  @media (max-width: 768px) and (max-height: 600px) {
    margin-top: 40px;
  }
`;

const Decoration = styled.div`
  position: absolute;
  z-index: 1;
  top: ${props => props.$position.top || '10%'};
  left: ${props => props.$position.left || 'auto'};
  right: ${props => props.$position.right || 'auto'};
  bottom: ${props => props.$position.bottom || 'auto'};
  animation: ${float} 3s ease-in-out infinite;
  
  ${media.md`
    display: ${props => props.$hideOnMobile ? 'none' : 'block'};
  `}
`;

const Badge = styled.div`
  background: ${props => props.$bgColor || 'rgba(26, 188, 156, 0.1)'};
  color: ${props => props.$color || '#1ABC9C'};
  padding: 8px 16px;
  border-radius: 100px;
  font-weight: 600;
  font-size: 14px;
  letter-spacing: 1px;
  
  ${media.md`
    font-size: 12px;
    padding: 6px 12px;
  `}
`;

const FloatingDecoration = memo(() => {
  const { t } = useTranslation();
  const lastScrollY = useRef(window.scrollY);
  
  // Все features всегда доступны, CSS скроет лишние
  const features = ['security', 'rewards', 'community', 'loyalty'];

  useEffect(() => {
    const handleScroll = () => {
      requestAnimationFrame(() => {
        const footer = document.querySelector('footer');
        const box = document.querySelector('.floating-box');
        if (!footer || !box) return;

        const footerRect = footer.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const isMobile = window.innerWidth <= 768;

        if (footerRect.top <= windowHeight) {
          const distance = windowHeight - footerRect.top;
          if (isMobile) {
            box.classList.add('near-footer');
          } else {
            box.style.transform = `translateY(-${distance}px)`;
          }
        } else {
          if (isMobile) {
            box.classList.remove('near-footer');
          } else {
            box.style.transform = 'translateY(0)';
          }
        }

        lastScrollY.current = window.scrollY;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <FloatingBox className="floating-box">
        <Title>
          <h3>
            {t('floating.title.line1')} <br />
            <span className="highlight">{t('floating.title.highlight')}</span> <br />
            {t('floating.title.line2')}
          </h3>
        </Title>
        
        <Features>
          {features.map((feature, index) => (
            <Feature key={index}>
              <Icon name={feature} />
              <FeatureContent>
                <h4>{t(`floating.features.${feature}.title`)}</h4>
                <p>{t(`floating.features.${feature}.description`)}</p>
              </FeatureContent>
            </Feature>
          ))}
        </Features>
        
        <SocialLinks>
          <SocialLink 
            href="https://t.me/todealmarket" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <Icon name="telegram" />
            <span>Telegram</span>
          </SocialLink>
          <SocialLink 
            href="https://x.com/todealmarket" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <Icon name="twitter" />
            <span>Twitter</span>
          </SocialLink>
          {/* <SocialLink 
            href="https://www.reddit.com/user/Sad-Engine2774" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <Icon name="reddit" />
            <span>Reddit</span>
          </SocialLink> */}
        </SocialLinks>
        
        <MobileSocialLinks>
          <MobileSocialLink 
            href="https://t.me/todealmarket" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <Icon name="telegram" />
          </MobileSocialLink>
          <MobileSocialLink 
            href="https://x.com/todealmarket" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <Icon name="twitter" />
          </MobileSocialLink>
          <MobileSocialLink 
            href="https://www.reddit.com/user/Sad-Engine2774" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <Icon name="reddit" />
          </MobileSocialLink>
        </MobileSocialLinks>
        
        {/* <ActionButton 
          href="https://t.me/blum/app?startapp=memepadjetton_TDM_FdK7I-ref_wXl79rdCYO"
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('buttons.buyToken')}
        </ActionButton> */}
      </FloatingBox>
      <div className="floating-decoration">
        <style jsx>{`
          .floating-decoration {
            position: relative;
            width: 100%;
            height: 100%;
            overflow: hidden;
          }

          @media (max-width: 768px) {
            .floating-decoration::before,
            .floating-decoration::after {
              content: '';
              position: absolute;
              width: 150px; // Уменьшаем размер с 300px
              height: 150px; // Уменьшаем размер с 300px
              border-radius: 50%;
              filter: blur(100px);
              z-index: -1;
            }

            .floating-decoration::before {
              background: rgba(255, 59, 159, 0.3);
              top: 70%; // Корректируем позицию
              left: -50px;
            }

            .floating-decoration::after {
              background: rgba(43, 99, 218, 0.3);
              bottom: 60%; // Корректируем позицию
              right: -50px;
            }
          }
        `}</style>
      </div>
    </>
  );
});

FloatingDecoration.displayName = 'FloatingDecoration';

export default FloatingDecoration; 